import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (n) => Number(n).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
const fmtDate = (iso) => new Date(iso).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" });

export default function Admin() {
  const [code, setCode] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/orders`, { headers: { "X-Admin-Code": code } });
      setData(res.data);
    } catch (err) {
      setError(err.response?.status === 403 ? "Code incorrect, réessayez." : "Erreur de chargement, réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen luminous">
      <header className="border-b hairline-gold">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" data-testid="admin-logo" className="font-serif text-xl tracking-[0.25em] text-choco">KARYNA</Link>
          <Link to="/" data-testid="admin-back" className="text-xs uppercase tracking-[0.2em] text-choco/75 hover:text-gold transition-colors">
            ← Retour à la boutique
          </Link>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-12" data-testid="admin-page">
        {!data ? (
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            onSubmit={load}
            className="max-w-md mx-auto mt-16 text-center"
            data-testid="admin-login-form"
          >
            <Lock className="mx-auto text-gold" size={28} strokeWidth={1.5} />
            <h1 className="font-serif text-3xl font-light text-choco mt-6">Votre espace privé</h1>
            <p className="mt-3 text-sm text-choco/75 font-light">Entrez votre code confidentiel pour voir vos commandes et vos inscrites à la lettre.</p>
            <input
              data-testid="admin-code-input"
              type="password"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Code confidentiel"
              className="mt-8 w-full bg-transparent border border-choco/25 focus:border-gold outline-none px-4 py-3 text-center text-choco tracking-widest transition-colors"
            />
            {error && <p data-testid="admin-error" className="mt-3 text-sm text-red-700">{error}</p>}
            <button
              data-testid="admin-login-button"
              type="submit"
              disabled={loading}
              className="mt-6 w-full bg-choco text-ivory text-xs uppercase tracking-[0.25em] py-4 hover:bg-denim transition-colors duration-400 disabled:opacity-50"
            >
              {loading ? "Vérification…" : "Voir mes commandes"}
            </button>
          </motion.form>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
            <h1 className="font-serif text-3xl font-light text-choco">Bonjour Karine ✦</h1>
            <p className="mt-2 text-sm text-choco/75 font-light" data-testid="admin-counts">
              {data.orders.length} commande{data.orders.length > 1 ? "s" : ""} — {data.newsletter.length} inscrite{data.newsletter.length > 1 ? "s" : ""} à la lettre
            </p>
            <h2 className="text-xs uppercase tracking-[0.3em] text-gold mt-10 mb-5">Commandes reçues</h2>
            <div className="space-y-5" data-testid="admin-orders-list">
              {data.orders.length === 0 && <p className="text-sm text-choco/65 font-light">Aucune commande pour le moment.</p>}
              {data.orders.map((o) => (
                <div key={o.id} data-testid={`admin-order-${o.number}`} className="bg-white/60 border hairline-gold p-6">
                  <div className="flex flex-wrap items-baseline justify-between gap-3">
                    <p className="font-serif text-lg text-choco">{o.number}</p>
                    <p className="text-xs text-choco/65">{fmtDate(o.created_at)}</p>
                  </div>
                  <p className="mt-2 text-sm text-choco/90 font-light">
                    {o.customer.prenom} {o.customer.nom} — {o.customer.email} — {o.customer.adresse}, {o.customer.code_postal} {o.customer.ville}
                  </p>
                  <ul className="mt-3 space-y-1 text-sm font-light text-choco/85">
                    {o.items.map((i) => (
                      <li key={i.slug + (i.size || "") + (i.color || "")}>{i.qty} × {i.name}{i.size ? ` — Taille ${i.size}` : ""}{i.color ? ` — ${i.color}` : ""} — {eur(i.price * i.qty)}</li>
                    ))}
                  </ul>
                  <div className="mt-3 pt-3 border-t hairline-gold flex justify-between text-sm">
                    <span className="text-choco/75">
                      {o.promo_code ? <>Code {o.promo_code} (−{eur(o.discount)}) · </> : null}
                      Paiement : à finaliser avec la cliente
                    </span>
                    <span className="font-serif text-choco text-base">{eur(o.total)}</span>
                  </div>
                </div>
              ))}
            </div>
            <h2 className="text-xs uppercase tracking-[0.3em] text-gold mt-12 mb-5">Inscrites à la lettre KARYNA</h2>
            <div data-testid="admin-newsletter-list" className="bg-white/60 border hairline-gold p-6">
              {data.newsletter.length === 0 && <p className="text-sm text-choco/65 font-light">Aucune inscrite pour le moment.</p>}
              <ul className="space-y-2 text-sm font-light text-choco/90">
                {data.newsletter.map((n) => (
                  <li key={n.email} className="flex justify-between gap-4">
                    <span>{n.email}</span>
                    <span className="text-xs text-choco/65">{fmtDate(n.created_at)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <h2 className="text-xs uppercase tracking-[0.3em] text-gold mt-12 mb-5">Messages reçus</h2>
            <div data-testid="admin-messages-list" className="space-y-5">
              {data.messages.length === 0 && <p className="text-sm text-choco/65 font-light">Aucun message pour le moment.</p>}
              {data.messages.map((m) => (
                <div key={m.id} data-testid={`admin-message-${m.id}`} className="bg-white/60 border hairline-gold p-6">
                  <div className="flex flex-wrap items-baseline justify-between gap-3">
                    <p className="font-serif text-lg text-choco">{m.nom}</p>
                    <p className="text-xs text-choco/65">{fmtDate(m.created_at)}</p>
                  </div>
                  <p className="mt-1 text-xs text-choco/75">{m.email}{m.telephone ? ` — ${m.telephone}` : ""}</p>
                  <p className="mt-3 text-sm font-light text-choco/90 leading-relaxed whitespace-pre-line">{m.message}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
